import { useCallback, useEffect, useRef, useState } from "react";
import { canonicalMovieLensId } from "../utils/selectableCatalog.mjs";

function readSessionValue(key, initial, sanitize) {
  try { return sanitize(JSON.parse(sessionStorage.getItem(key)) ?? initial); }
  catch { return initial; }
}

const identity = value => value;
export function useSessionValue(key, initial, sanitize = identity) {
  const current = useRef({ key, initial, sanitize });
  current.current = { key, initial, sanitize };
  const [entry, setEntry] = useState(() => ({ key, value: readSessionValue(key, initial, sanitize) }));
  const value = entry.key === key ? entry.value : readSessionValue(key, initial, sanitize);
  // Re-key before committing effects, so old values cannot be saved under the
  // new participant/session key, even when the component was not remounted.
  if (entry.key !== key) setEntry({ key, value });
  const setValue = useCallback(update => {
    if (current.current.key !== key) return; // Ignore a superseded async response.
    setEntry(previous => {
      const prior = previous.key === key ? previous.value
        : readSessionValue(key, current.current.initial, current.current.sanitize);
      const next = typeof update === "function" ? update(prior) : update;
      return previous.key === key && Object.is(prior, next) ? previous : { key, value: next };
    });
  }, [key]);
  useEffect(() => {
    if (entry.key !== key) return;
    try { sessionStorage.setItem(key, JSON.stringify(entry.value)); } catch { /* In-memory fallback. */ }
  }, [key, entry]);
  return [value, setValue];
}

export function useRecommendationSession(system, study, catalogIds) {
  const prefix = `recommendation-session-v4:${study.participantId}:${study.sessionId}:${system}`;
  const [recommendations, setRecommendations] = useSessionValue(`${prefix}:results`, []);
  const [history, setHistory] = useSessionValue(`${prefix}:shown`, []);
  const [notice, setNotice] = useSessionValue(`${prefix}:notice`, "");
  const [receipt, setReceipt] = useState(null);
  const recorded = useRef(new Set());
  const persist = useRef(study.persistRender);
  persist.current = study.persistRender;
  const [shownCatalog, setShownCatalog] = useSessionValue(
    `catalog-session-v4:${study.participantId}:${study.sessionId}`, catalogIds
  );
  const catalogRef = useRef(shownCatalog);
  catalogRef.current = shownCatalog;
  const recordCatalog = useCallback((movies) => {
    const merged = [...new Set([...catalogRef.current, ...movies.map(movie => canonicalMovieLensId(movie.movieId))])].filter(Boolean);
    if (merged.length !== catalogRef.current.length) {
      catalogRef.current = merged;
      setShownCatalog(merged);
    }
  }, [setShownCatalog]);

  // Effects run after the cards are committed to the DOM. Fetching a response,
  // rerendering, and restoring a saved batch do not create another batch.
  useEffect(() => {
    if (!recommendations.length) return;
    setHistory(previous => {
      const merged = [...new Set([...previous, ...recommendations.map(item => item.movie_id)])];
      return merged.length === previous.length ? previous : merged;
    });
    if (!receipt || receipt.prefix !== prefix || recorded.current.has(receipt.meta.request_id)) return;
    recorded.current.add(receipt.meta.request_id);
    persist.current(receipt.meta, recommendations).catch(() => {
      // Local shown IDs still protect the next request if receipt delivery fails.
      recorded.current.delete(receipt.meta.request_id);
    });
  }, [recommendations, receipt, setHistory, prefix]);

  const display = (items, meta) => { setRecommendations(items); setReceipt({ prefix, meta }); };
  const exclusions = (anotherBatch = false) => [...new Set([
    ...catalogIds, ...catalogRef.current, ...(anotherBatch ? history : []),
  ])];
  return { prefix, recommendations, setRecommendations, history, notice, setNotice, display, exclusions, recordCatalog };
}
